/**
 * Console shell (D-2602): collapsible sidebar grouped by job-to-be-done, slim top bar with the win-ID search,
 * command palette (⌘K), environment badge and a user menu; responsive drawer under 900px. Admin density is
 * higher than the participant app but every control keeps the touch floor on coarse pointers (§13.3) and
 * focus is visible everywhere (§13.5). Calm/sound/theme preferences live in the Display dialog.
 */
import { isValidWinId, normalizeWinId } from '@windrop/contracts';
import { Button, Modal, Switch, VisuallyHidden, useEnergy } from '@windrop/ui';
import {
  ChevronDown,
  ExternalLink,
  Keyboard,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Settings2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useSession } from '../auth/session';
import { readRuntimeConfig, type AppEnv } from '../lib/runtime-config';
import { useConsoleTheme, type ThemePreference } from '../lib/theme';
import { CommandPalette } from './CommandPalette';
import { NAV_ICONS } from './icons';
import { NAV_ITEMS, SECTION_LABELS, SECTION_ORDER, visibleNav, type NavItem } from './navigation';

const ENV_LABEL: Record<AppEnv, string> = { dev: 'Development', staging: 'Staging', prod: 'Production' };
const SIDEBAR_KEY = 'windrop:console-sidebar';

export function EnvBadge() {
  const { appEnv, buildSha } = readRuntimeConfig();
  return (
    <span
      className="wdc-env"
      data-env={appEnv}
      title={`Build ${buildSha}`}
      aria-label={`${ENV_LABEL[appEnv]} environment, build ${buildSha}`}
    >
      {ENV_LABEL[appEnv]}
    </span>
  );
}

const isTypingTarget = (t: EventTarget | null): boolean =>
  t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);

export function GlobalSearch() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && !isTypingTarget(e.target) && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const normalized = normalizeWinId(value.trim());
    if (!normalized || !isValidWinId(normalized)) {
      setError('Enter a win ID such as WIN-XXXXX-XXXXX. Mismatches are reported, never guessed.');
      return;
    }
    setError(null);
    setValue('');
    navigate(`/winners/lookup/${normalized}`);
  };

  return (
    <form
      role="search"
      className="wdc-search"
      onSubmit={submit}
      aria-describedby={error ? `${id}-err` : undefined}
    >
      <label htmlFor={id} className="wd-sr-only">
        Look up a win ID
      </label>
      <div className="wdc-search__box">
        <Search size={16} aria-hidden="true" />
        <input
          ref={inputRef}
          id={id}
          className="wd-input"
          placeholder="Look up a win ID"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError(null);
          }}
          autoComplete="off"
          spellCheck={false}
          aria-invalid={error ? true : undefined}
          aria-keyshortcuts="/"
        />
        <span className="wdc-search__kbd" aria-hidden="true">
          <kbd>/</kbd>
        </span>
      </div>
      <Button type="submit" variant="secondary" className="wdc-search__submit">
        Look up
      </Button>
      {error ? (
        <p id={`${id}-err`} role="alert" className="wdc-search__error">
          {error}
        </p>
      ) : null}
    </form>
  );
}

export function ShortcutsHelp({
  open,
  onOpenChange,
  items,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  items: NavItem[];
}) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Keyboard shortcuts"
      description="Press g, then the letter, to jump. Shortcuts never fire while you type in a field."
    >
      <dl className="wdc-shortcuts">
        <div>
          <dt className="wdc-shortcuts__group">Global</dt>
          <dd className="wd-sr-only">Global shortcuts</dd>
        </div>
        <div>
          <dt>
            <kbd>⌘</kbd>
            <kbd>K</kbd>
          </dt>
          <dd>Open the command palette (Ctrl+K on Windows and Linux)</dd>
        </div>
        <div>
          <dt>
            <kbd>/</kbd>
          </dt>
          <dd>Focus the win ID search</dd>
        </div>
        <div>
          <dt>
            <kbd>?</kbd>
          </dt>
          <dd>Open this help</dd>
        </div>
        <div>
          <dt>
            <kbd>[</kbd>
          </dt>
          <dd>Collapse or expand the sidebar</dd>
        </div>
        <div>
          <dt className="wdc-shortcuts__group">Jump to</dt>
          <dd className="wd-sr-only">Navigation shortcuts</dd>
        </div>
        {items
          .filter((i) => i.shortcut)
          .map((i) => (
            <div key={i.id}>
              <dt>
                <kbd>g</kbd> <kbd>{i.shortcut}</kbd>
              </dt>
              <dd>{i.label}</dd>
            </div>
          ))}
      </dl>
    </Modal>
  );
}

const THEME_OPTIONS: { value: ThemePreference; label: string; hint: string }[] = [
  { value: 'system', label: 'System', hint: 'Light unless your OS asks for dark' },
  { value: 'light', label: 'Light', hint: 'Always light' },
  { value: 'dark', label: 'Dark', hint: 'Always dark' },
];

export function PreferencesDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const energy = useEnergy();
  const theme = useConsoleTheme();
  const groupId = useId();
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Display preferences"
      description="These follow you on this device only."
    >
      <div className="wd-stack">
        <fieldset className="wd-stack" style={{ border: 0, padding: 0, margin: 0, gap: 8 }}>
          <legend className="wd-field__label" id={`${groupId}-legend`}>
            Theme
          </legend>
          <div className="wdc-chips" role="radiogroup" aria-labelledby={`${groupId}-legend`}>
            {THEME_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={theme.preference === o.value}
                className="wdc-chip"
                aria-pressed={theme.preference === o.value}
                title={o.hint}
                onClick={() => theme.setPreference(o.value)}
              >
                {o.label}
              </button>
            ))}
          </div>
          <p className="wd-muted wdc-small">
            Currently {theme.resolved}. {THEME_OPTIONS.find((o) => o.value === theme.preference)?.hint}.
          </p>
        </fieldset>
        <Switch
          label="Calm mode (reduced motion, no confetti)"
          checked={energy.userPreference === 'CALM' || (energy.userPreference === 'SYSTEM' && energy.calm)}
          onCheckedChange={(on) => energy.setUserPreference(on ? 'CALM' : 'FULL')}
        />
        <Switch
          label="Sound effects"
          checked={energy.soundEnabled}
          onCheckedChange={energy.setSoundEnabled}
        />
        <p className="wd-muted wdc-small">
          Current motion level: <strong>{energy.energy === 'CALM' ? 'Calm' : 'Full'}</strong>
          {energy.prefersReducedMotion ? ' (your system asks for reduced motion)' : ''}.
        </p>
      </div>
    </Modal>
  );
}

export const humanizeRole = (role: string): string =>
  role
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

export const initials = (name: string | undefined | null): string => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  return parts
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
};

function UserMenu({
  onOpenPreferences,
  onOpenShortcuts,
}: {
  onOpenPreferences: () => void;
  onOpenShortcuts: () => void;
}) {
  const { session, logout } = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const roles = session?.roles.map(humanizeRole).join(', ') ?? '';
  return (
    <div className="wdc-menu" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="wd-btn wdc-user"
        data-variant="quiet"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={`${id}-menu`}
        onClick={() => setOpen((o) => !o)}
        title={session?.email}
      >
        <span className="wdc-avatar" aria-hidden="true">
          {initials(session?.name)}
        </span>
        <span className="wdc-user__text">
          <span className="wdc-user__name">{session?.name}</span>
          <span className="wdc-user__role">{roles}</span>
        </span>
        <VisuallyHidden>Account menu</VisuallyHidden>
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {open ? (
        <ul id={`${id}-menu`} role="menu" className="wdc-menu__list">
          <li role="presentation" className="wdc-menu__head">
            <strong>{session?.name}</strong>
            {session?.email}
            <br />
            {roles}
            {session?.tenantSlug ? ` · ${session.tenantSlug}` : ''}
          </li>
          <li role="none">
            <button
              type="button"
              role="menuitem"
              className="wdc-menu__item"
              onClick={() => {
                setOpen(false);
                onOpenPreferences();
              }}
            >
              <Settings2 size={16} aria-hidden="true" /> Display preferences
            </button>
          </li>
          <li role="none">
            <button
              type="button"
              role="menuitem"
              className="wdc-menu__item"
              onClick={() => {
                setOpen(false);
                onOpenShortcuts();
              }}
            >
              <Keyboard size={16} aria-hidden="true" /> Keyboard shortcuts <kbd>?</kbd>
            </button>
          </li>
          <li role="none">
            <button
              type="button"
              role="menuitem"
              className="wdc-menu__item"
              onClick={async () => {
                setOpen(false);
                await logout();
                navigate('/login', { replace: true });
              }}
            >
              <LogOut size={16} aria-hidden="true" /> Sign out
            </button>
          </li>
        </ul>
      ) : null}
    </div>
  );
}

const readCollapsed = (): boolean => {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === 'collapsed';
  } catch {
    return false;
  }
};

export function AppShell({ children }: { children?: ReactNode }) {
  const { session } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed());
  const [helpOpen, setHelpOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const items = visibleNav(session);
  const { webOrigin } = readRuntimeConfig();
  const navRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? 'collapsed' : 'expanded');
      } catch {
        /* device-only preference */
      }
      return next;
    });
  }, []);

  // Close the drawer on navigation (mobile).
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Drawer: lock scroll, Escape closes, focus moves into the drawer and back to the menu button.
  useEffect(() => {
    if (!navOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const first = navRef.current?.querySelector<HTMLElement>('a, button');
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setNavOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [navOpen]);

  // `?` → help, `[` → collapse, ⌘K → palette, `g` + letter → navigate.
  useEffect(() => {
    let pendingG = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      if (isTypingTarget(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === '?') {
        e.preventDefault();
        setHelpOpen(true);
        return;
      }
      if (e.key === '[') {
        e.preventDefault();
        toggleCollapsed();
        return;
      }
      if (pendingG) {
        pendingG = false;
        const item = items.find((i) => i.shortcut === e.key.toLowerCase());
        if (item) {
          e.preventDefault();
          navigate(item.to);
        }
        return;
      }
      if (e.key === 'g') {
        pendingG = true;
        clearTimeout(timer);
        timer = setTimeout(() => {
          pendingG = false;
        }, 1500);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(timer);
    };
  }, [items, navigate, toggleCollapsed]);

  const sections = SECTION_ORDER.filter((s) => items.some((i) => i.section === s));
  const tenant = session?.tenantSlug ?? 'Windrop';

  return (
    <div
      className="wdc-shell"
      data-nav-open={navOpen ? 'true' : undefined}
      data-collapsed={collapsed ? 'true' : undefined}
    >
      <a href="#main" className="wdc-skip">
        Skip to main content
      </a>
      <nav id="console-nav" className="wdc-sidebar" aria-label="Console" ref={navRef}>
        <div className="wdc-sidebar__brand">
          <span className="wdc-brand__mark" aria-hidden="true">
            W
          </span>
          <span className="wdc-brand__text">
            <span className="wdc-brand__tenant" title={tenant}>
              {tenant}
            </span>
            <span className="wdc-brand__product">Windrop console</span>
          </span>
          <button
            type="button"
            className="wd-btn wdc-iconbtn wdc-sidebar__collapse"
            data-variant="quiet"
            onClick={toggleCollapsed}
            aria-pressed={collapsed}
            aria-keyshortcuts="["
            title={collapsed ? 'Expand sidebar ([)' : 'Collapse sidebar ([)'}
          >
            {collapsed ? (
              <PanelLeftOpen size={18} aria-hidden="true" />
            ) : (
              <PanelLeftClose size={18} aria-hidden="true" />
            )}
            <VisuallyHidden>{collapsed ? 'Expand sidebar' : 'Collapse sidebar'}</VisuallyHidden>
          </button>
        </div>
        <div className="wdc-sidebar__scroll">
          {sections.map((section) => (
            <div key={section} className="wdc-sidebar__section">
              <h2 className="wdc-sidebar__heading">{SECTION_LABELS[section]}</h2>
              <ul>
                {items
                  .filter((i) => i.section === section)
                  .map((item) => {
                    const Icon = NAV_ICONS[item.id];
                    return (
                      <li key={item.id}>
                        <NavLink
                          to={item.to}
                          end={item.to === '/'}
                          className="wdc-navlink"
                          title={collapsed ? item.label : undefined}
                        >
                          <span className="wdc-navlink__icon">
                            {Icon ? <Icon size={17} aria-hidden="true" /> : null}
                          </span>
                          <span className="wdc-navlink__label">{item.label}</span>
                          {item.shortcut ? (
                            <span className="wdc-navlink__key" aria-hidden="true">
                              g {item.shortcut}
                            </span>
                          ) : null}
                        </NavLink>
                      </li>
                    );
                  })}
              </ul>
            </div>
          ))}
        </div>
        <div className="wdc-sidebar__foot">
          {session?.tenantSlug ? (
            <a
              className="wdc-footlink"
              href={`${webOrigin}/`}
              target="_blank"
              rel="noreferrer"
              title={collapsed ? 'Open participant site' : undefined}
            >
              <span className="wdc-navlink__icon">
                <ExternalLink size={16} aria-hidden="true" />
              </span>
              <span className="wdc-navlink__label">Open participant site</span>
            </a>
          ) : null}
          <button
            type="button"
            className="wdc-footlink"
            onClick={() => setHelpOpen(true)}
            title={collapsed ? 'Keyboard shortcuts' : undefined}
          >
            <span className="wdc-navlink__icon">
              <Keyboard size={16} aria-hidden="true" />
            </span>
            <span className="wdc-navlink__label">Keyboard shortcuts</span>
            <span className="wdc-navlink__key" aria-hidden="true">
              ?
            </span>
          </button>
          <div className="wdc-sidebar__hint" aria-hidden="true">
            <span>Jump anywhere</span>
            <kbd>⌘K</kbd>
          </div>
        </div>
      </nav>
      <button
        type="button"
        className="wdc-backdrop"
        aria-label="Close navigation"
        tabIndex={-1}
        onClick={() => setNavOpen(false)}
      />
      <header className="wdc-topbar">
        <button
          ref={menuButtonRef}
          type="button"
          className="wd-btn wdc-iconbtn wdc-topbar__menu"
          data-variant="quiet"
          aria-expanded={navOpen}
          aria-controls="console-nav"
          onClick={() => setNavOpen((o) => !o)}
        >
          {navOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
          <VisuallyHidden>{navOpen ? 'Close navigation' : 'Open navigation'}</VisuallyHidden>
        </button>
        <span className="wdc-topbar__brand">
          <span className="wdc-brand__mark" aria-hidden="true">
            W
          </span>
          {tenant}
        </span>
        {session && items.some((i) => i.id === 'winners') ? (
          <GlobalSearch />
        ) : (
          <span className="wdc-topbar__spacer" />
        )}
        <div className="wdc-topbar__actions">
          <EnvBadge />
          <Button
            variant="quiet"
            onClick={() => setPaletteOpen(true)}
            icon={<Search size={16} />}
            aria-keyshortcuts="Meta+K Control+K"
            title="Command palette (⌘K)"
          >
            <span className="wdc-topbar__label">Jump to</span>
            <kbd className="wdc-topbar__label" aria-hidden="true">
              ⌘K
            </kbd>
          </Button>
          <UserMenu onOpenPreferences={() => setPrefsOpen(true)} onOpenShortcuts={() => setHelpOpen(true)} />
        </div>
      </header>
      <main id="main" className="wdc-main" tabIndex={-1}>
        {children ?? <Outlet />}
      </main>
      <ShortcutsHelp open={helpOpen} onOpenChange={setHelpOpen} items={items} />
      <PreferencesDialog open={prefsOpen} onOpenChange={setPrefsOpen} />
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onOpenShortcuts={() => setHelpOpen(true)}
        onOpenPreferences={() => setPrefsOpen(true)}
      />
    </div>
  );
}

export { NAV_ITEMS };
